import { type Dispatch, type FC, type SetStateAction, useRef } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import { type LeagueRewardType } from '@/common/types/league';
import { ticketRewardToTier } from '@/components/Lottery/config';
import { StringSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import { TicketIcon } from '@/components/ui/TicketIcon/TicketIcon';

import { chestImages } from '../config';

import toncoinPng from '/img/icons/toncoin.png';
import toncoinWebp from '/img/icons/toncoin.webp';
import usdtPng from '/img/icons/usd.png';
import usdtWebp from '/img/icons/usd.webp';

interface PositionItemRewardsProps {
  rewards?: LeagueRewardType['rewards'];
  kind: 'gold' | 'silver' | 'bronze' | 'default';
  setPositionRewardIsOpen: Dispatch<SetStateAction<boolean>>;
  setTopOffset: () => void;
}

const PositionItemRewards: FC<PositionItemRewardsProps> = ({
  rewards,
  kind,
  setPositionRewardIsOpen,
  setTopOffset,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useOnClickOutside(ref, () => {
    setPositionRewardIsOpen(false);
  });

  useOnClickOutside(
    ref,
    () => {
      setPositionRewardIsOpen(false);
    },
    'touchstart',
  );

  const onChestClick = (): void => {
    setTopOffset();
    setPositionRewardIsOpen((state) => !state);
  };

  const sortTickets = (a: [string, number], b: [string, number]): number =>
    a[0][0] < b[0][0] ? -1 : 1;

  return (
    <>
      <div className="gap-0.5 flex items-center justify-start mr-1.5">
        {!!rewards?.wallet.ton && (
          <>
            <Picture
              className="size-6 shrink-0"
              img={toncoinPng}
              webp={toncoinWebp}
              alt="toncoin"
            />
            <StringSmall weight="bold">{rewards?.wallet.ton}</StringSmall>
          </>
        )}
        {!!rewards?.wallet.usdt && (
          <>
            <Picture
              className="size-6 shrink-0"
              img={usdtPng}
              webp={usdtWebp}
              alt="usdt"
            />
            <StringSmall weight="bold">{rewards?.wallet.usdt}</StringSmall>
          </>
        )}
        {(!!rewards?.lottery || !!rewards?.lotteries) &&
          Object.entries(rewards?.lottery || rewards?.lotteries || {})
            .sort(sortTickets)
            .map(([ticketName, value]) => (
              <div className="w-[35px] flex justify-end items-center gap-[2px]">
                <TicketIcon
                  className="size-[20px]"
                  type={
                    ticketRewardToTier[
                      ticketName as keyof typeof ticketRewardToTier
                    ]
                  }
                />
                <StringSmall weight="bold">{value}</StringSmall>
              </div>
            ))}
      </div>
      <div
        onClick={onChestClick}
        ref={ref}
        className="relative flex items-center justify-center"
      >
        <Picture
          className="size-10"
          img={chestImages[kind].png}
          webp={chestImages[kind].webp}
          alt={kind}
        />
      </div>
    </>
  );
};

export { PositionItemRewards };
